import { endOfMonth } from 'date-fns';
import { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';
import { Button, Spinner, SpotlightOutline, Stack, useDisclosure, useToast } from '@bonusly/design-system';

import { useCreateSpotlight, useEmployeeSpotlightsForUser, useSpotlightEligibility } from '../../../../../queries/employee-spotlights';
import { AwardType, Bonus } from '../../../../../types/bonus';
import { CreateCelebrationSpotlightModal } from '../../../../spotlight/user/create-celebration-spotlight-modal';

type Props = {
	bonus: Bonus;
};

const EMPLOYEE_SPOTLIGHT_CATEGORY = 'employee_spotlight';

const getWorkAnniversaryReceiver = (bonus: Bonus): Bonus['receivers'][number] | undefined => {
	if (bonus.award_type !== AwardType.WorkAnniversary) return undefined;
	const receivers = bonus.receivers ?? [];
	if (receivers.length !== 1) return undefined;
	return receivers[0];
};

const getFirstNameFromDisplayName = (displayName?: string): string | undefined => {
	const name = displayName?.trim();
	if (!name) return undefined;
	return name.split(/\s+/)[0];
};

const hasEmployeeSpotlightAttachment = (bonus: Bonus): boolean => {
	const hasOnBonus = (bonus.attachments ?? []).some((attachment) => attachment?.category === EMPLOYEE_SPOTLIGHT_CATEGORY);
	if (hasOnBonus) return true;

	// Employee snapshot attachments may be present on comment/add-on bonuses in the thread.
	return (bonus.child_bonuses ?? []).some((child) => hasEmployeeSpotlightAttachment(child));
};

export const WorkAnniversarySnapshotCTA: FC<Props> = ({ bonus }) => {
	const { t } = useTranslation('spotlight');
	const toast = useToast();
	const { track } = useAnalytics();
	const confirmModal = useDisclosure();
	const [isGeneratingLocal, setIsGeneratingLocal] = useState(false);
	const isWorkAnniversary = bonus.award_type === AwardType.WorkAnniversary;
	const bonusOrCommentsAlreadyHaveSnapshot = useMemo(() => hasEmployeeSpotlightAttachment(bonus), [bonus]);

	const receiver = useMemo(() => getWorkAnniversaryReceiver(bonus), [bonus]);

	const receiverFirstName = useMemo(() => {
		return getFirstNameFromDisplayName(receiver?.display_name);
	}, [receiver?.display_name]);

	const startTime = receiver?.hired_on ?? '';
	// Use end of month to match the manual modal behavior - users see end of month as the end date
	const endTime = bonus.created_at ? endOfMonth(new Date(bonus.created_at)).toISOString() : '';

	// Query eligibility from backend - includes authorization check (manager/admin)
	const shouldQueryEligibility = Boolean(isWorkAnniversary && receiver?.id && startTime && endTime && !bonusOrCommentsAlreadyHaveSnapshot);

	const eligibility = useSpotlightEligibility(receiver?.id ?? '', startTime, endTime, {
		enabled: shouldQueryEligibility,
		staleTime: Infinity, // Don't refetch eligibility unless the user explicitly requests a new snapshot.
	});

	// Check if there's already a pending/processing spotlight for this user (survives page refresh)
	const { data: existingSpotlights } = useEmployeeSpotlightsForUser(receiver?.id ?? '');
	const hasExistingPendingSpotlight = useMemo(() => {
		if (!existingSpotlights || !startTime || !endTime) return false;
		return existingSpotlights.some(
			(s) =>
				(s.status === 'pending' || s.status === 'processing') &&
				s.startTime?.toISOString() === new Date(startTime).toISOString() &&
				s.endTime?.toISOString() === new Date(endTime).toISOString()
		);
	}, [existingSpotlights, startTime, endTime]);

	// Show generating state if either local state is set or there's a pending spotlight in the database
	const isGenerating = isGeneratingLocal || hasExistingPendingSpotlight;

	// Show CTA only if eligible (backend checks authorization and recognition data)
	const showCTA = Boolean(isWorkAnniversary && receiver?.id && startTime && endTime && eligibility.data?.eligible && !bonusOrCommentsAlreadyHaveSnapshot);
	const createSpotlight = useCreateSpotlight();

	const onConfirm = useCallback(() => {
		if (!receiver?.id) return;

		track(AnalyticsEvent.Spotlight.WorkAnniversaryModalGenerateClicked, { receiverId: receiver.id });

		createSpotlight.mutate(
			{
				user_id: receiver.id,
				start_time: startTime,
				end_time: endTime,
				bonus_id: bonus.id, // Auto-attach spotlight to this celebration bonus
			},
			{
				onSuccess: () => {
					confirmModal.onClose();
					setIsGeneratingLocal(true);
					toast({
						status: 'success',
						title: t('createCelebrationSpotlightModal.toasts.generatingTitle'),
						isClosable: true,
					});
				},
				onError: (error) => {
					toast({
						status: 'error',
						title: t('createCelebrationSpotlightModal.toasts.errorTitle'),
						description: error instanceof Error ? error.message : t('createCelebrationSpotlightModal.toasts.errorFallback'),
						isClosable: true,
					});
				},
			}
		);
	}, [bonus.id, confirmModal, createSpotlight, endTime, receiver?.id, startTime, t, toast, track]);

	if (!showCTA) {
		return null;
	}

	return (
		<>
			<Stack>
				<Button
					width="100%"
					leftIcon={isGenerating ? <Spinner size="sm" /> : <SpotlightOutline h={6} w={6} />}
					onClick={() => {
						track(AnalyticsEvent.Spotlight.WorkAnniversaryCTAClicked, { receiverId: receiver?.id ?? '' });
						confirmModal.onOpen();
					}}
					isDisabled={isGenerating}
					colorScheme="secondary"
					variant="solid"
				>
					{isGenerating
						? t('workAnniversaryCta.generating')
						: receiverFirstName
							? t('workAnniversaryCta.buttonWithName', { name: receiverFirstName })
							: t('workAnniversaryCta.button')}
				</Button>
			</Stack>

			<CreateCelebrationSpotlightModal
				isOpen={confirmModal.isOpen}
				onClose={confirmModal.onClose}
				onConfirm={onConfirm}
				isLoading={createSpotlight.isLoading}
			/>
		</>
	);
};
